import type { ActivityCommentScope } from './activity-comment-scope';
import type { ActivityChatItem } from './activity-message';

/**
 * Источник запуска команды/поверхности в Activity.
 */
export type ActivityCommandSource = 'slash' | 'comment' | 'pin';

/**
 * Универсальный payload команды/поверхности.
 */
export type ActivityCommandPayload = Record<string, unknown>;

/**
 * Контекст запуска команды, который передаётся в `isAvailable` и форму команды.
 */
export type ActivityCommandPreviewContext = {
  threadEntityType?: string | null;
  threadEntityUuid?: string | null;
  source?: ActivityCommandSource;
  event?: ActivityChatItem | null;
  payload?: ActivityCommandPayload | null;
};

/**
 * Известная часть payload для task-пинов.
 */
export type ActivityTaskPinPayload = ActivityCommandPayload & {
  task_id?: unknown;
  taskId?: unknown;
  task_uuid?: unknown;
  task_type_id?: unknown;
  entity_type?: unknown;
  entity_uuid?: unknown;
  scope_label?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toPositiveInt(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.trunc(parsed);
}

function toTrimmedString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

/**
 * Приводит произвольное значение к payload-объекту команды.
 */
export function normalizeActivityCommandPayload(payload: unknown): ActivityCommandPayload | null {
  if (!isRecord(payload)) return null;
  return payload;
}

/**
 * Возвращает task_id из payload (`task_id` или `taskId`).
 */
export function extractActivityTaskId(payload: unknown): number | null {
  const normalized = normalizeActivityCommandPayload(payload);
  if (!normalized) return null;
  const taskPayload: ActivityTaskPinPayload = normalized;
  return toPositiveInt(taskPayload.task_id ?? taskPayload.taskId);
}

/**
 * Возвращает task_type_id из payload.
 */
export function extractActivityTaskTypeId(payload: unknown): number | null {
  const normalized = normalizeActivityCommandPayload(payload);
  if (!normalized) return null;
  const taskPayload: ActivityTaskPinPayload = normalized;
  return toPositiveInt(taskPayload.task_type_id);
}

/**
 * Возвращает true, если payload содержит валидный task_id.
 */
export function hasActivityTaskId(payload: unknown): boolean {
  return extractActivityTaskId(payload) !== null;
}

/**
 * Извлекает domain-scope (`entity_type/entity_uuid/scope_label`) из payload.
 */
export function extractActivityScopeFromPayload(payload: unknown): ActivityCommentScope | null {
  const normalized = normalizeActivityCommandPayload(payload);
  if (!normalized) return null;
  const taskPayload: ActivityTaskPinPayload = normalized;

  const entityType = toTrimmedString(taskPayload.entity_type);
  const entityUuid = toTrimmedString(taskPayload.entity_uuid);
  if (!entityType || !entityUuid) return null;

  const scopeLabel = toTrimmedString(taskPayload.scope_label);
  return {
    entity_type: entityType,
    entity_uuid: entityUuid,
    ...(scopeLabel ? { label: scopeLabel } : {}),
  };
}

/**
 * Строит task-scope (`tasks::TaskRepository`) из payload задачи.
 */
export function extractActivityTaskScopeFromPayload(payload: unknown): ActivityCommentScope | null {
  const normalized = normalizeActivityCommandPayload(payload);
  if (!normalized) return null;
  const taskPayload: ActivityTaskPinPayload = normalized;

  const taskUuid = toTrimmedString(taskPayload.task_uuid);
  const taskId = extractActivityTaskId(taskPayload);
  if (!taskUuid || !taskId) return null;

  return {
    entity_type: 'tasks::TaskRepository',
    entity_uuid: taskUuid,
    label: `Задача #${taskId}`,
  };
}
